import { useUserIdWithLoading } from '@dreamstack/auth'
import { useFormat, useLocale, useTranslation } from '@dreamstack/i18n'
import { assertPopulated } from '@dreamstack/payload/src/lib/assertPopulated'
import { getOrCreateAnonymousId } from '@dreamstack/tracking/src/getOrCreateAnonymousId'
import { RouterOutputs, trpc } from '@dreamstack/trpc/client'
import Image from 'next/image'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import { FadeIn } from '../layout/FadeIn'
import { BodyText, HeadingH3 } from '../layout/Texts'
import { ContinueReadingLink } from './ContinueReadingLink'
import {
  StyledNewsDate,
  StyledNewsTypeTranslated,
  StyledNewsTypeWrapper,
} from './StyledNewsComponents'
import { getNewsPath } from './getNewsPath'

const StyledNewsDateWithMargin = (props: React.PropsWithChildren<{}>) => (
  <StyledNewsDate {...props} className="mt-px-16" />
)

const StyledWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="news-hub-preview-wrapper">
    <style>{`.news-hub-preview-wrapper mark { @apply bg-accentroAquaVeryLight-full; }`}</style>
    {children}
  </div>
)

export const NewsHubPreview: FunctionComponent<
  React.PropsWithChildren<{
    article: NonNullable<
      RouterOutputs['cms']['getNewsArticlePreviews']['items']
    >[number]
  }>
> = ({ article }) => {
  assertPopulated(article.image)
  const t = useTranslation()
  const formatDate = useFormat()
  const locale = useLocale()
  const { userId } = useUserIdWithLoading()
  const router = useRouter()
  const { mutateAsync: trackButtonClickServer } =
    trpc.anonymousTracking.trackEvent.useMutation()
  if (!article) return null
  const { slug, newsType, title, previewText, image, date, file, hideDate } =
    article
  if (!slug || !newsType) {
    // should never happen
    return null
  }
  const href = getNewsPath({ locale, slug, newsType: newsType })
  const formattedDate = formatDate(date, 'dd. MMMM yyyy')
  const translatedNewsType = t(`accentro:newsTypes.${newsType}`)

  const trackClickCallToActionButton = async () => {
    const anonymousUserId = getOrCreateAnonymousId()
    await trackButtonClickServer({
      eventName: 'buttonClick',
      userId,
      anonymousUserId,
      data: {
        buttonAction: 'Ratgeber für Immobilienerwerb',
        url: router.asPath,
        additionalInfo: {
          newsType,
          articleSlug: slug,
          articleTitle: title,
        },
      },
    })
  }

  return (
    <StyledWrapper>
      <FadeIn opacityOnly>
        {image && (
          <div className="relative h-px-320">
            <Image
              className="object-cover"
              src={image.url ?? ''}
              alt={image.alt || 'Newsarticle image'}
              layout="fill"
            />
          </div>
        )}
        {newsType && (
          <StyledNewsTypeWrapper>
            <StyledNewsTypeTranslated>
              {translatedNewsType}
            </StyledNewsTypeTranslated>
          </StyledNewsTypeWrapper>
        )}

        {typeof title === 'string' ? (
          <HeadingH3
            className="hyphens-auto"
            dangerouslySetInnerHTML={{ __html: title }}
          />
        ) : (
          <HeadingH3 className="hyphens-auto">{title}</HeadingH3>
        )}
        {date && !hideDate && (
          <StyledNewsDateWithMargin>
            {`Berlin, ${formattedDate}`}
          </StyledNewsDateWithMargin>
        )}

        <BodyText className="mt-px-32">{previewText}</BodyText>
        <ContinueReadingLink
          onClick={trackClickCallToActionButton}
          href={href}
          file={file}
        />
      </FadeIn>
    </StyledWrapper>
  )
}
